import { useModals } from "@/features/modals/hooks";
import { InstallationQuotationExistsFilter } from "@/features/projects/filter/InstallationQuotationExistsFilter";
import { AmountFilterOption } from "@/features/projects/filter/constants";
import { DateFilterPill } from "@/features/projects/filter/dateFilter/CreatedDateFilterPill";
import { DateFilterOption } from "@/features/projects/filter/dateFilter/utils";
import { useAiFeatureConfig } from "@/features/projects/hooks/useAiFeatureConfig";
import { useProjectTour } from "@/features/tour/projectTour/ProjectTourProvider";
import styled from "@emotion/styled";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  ProjectFilterDefault,
  type TeamSelection,
  initialOpportunityOptions,
  isTeamSelection,
  useTeam,
} from "@repo/api";
import {
  Pill,
  Variables,
  filtersIcon,
  plusIcon,
  sparklesIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoworkerAndTeamSelectorPill,
  FilterPill,
  FlexDiv,
  Line,
  ResponsiveButton,
  SearchField,
  SectionContainer,
  WidthAwareContainer,
  translateConsentType,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import {
  type ChangeEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { useResizeObserver, useWindowSize } from "usehooks-ts";

import { SortButton } from "../SortButton";
import { BokaServiceFilter } from "./BokaServiceFilter";
import { BusinessUnitFilter } from "./BusinessUnitFilter";
import { ClassificationFilter } from "./ClassificationFilter";
import { ConsentFilter } from "./ConsentFilter";
import { CustomerTypeFilter } from "./CustomerTypeFilter";
import { DepartmentFilter } from "./DepartmentFilter";
import { FilterSideBar } from "./FilterSidebar";
import { InstallationQuotationStatusFilter } from "./InstallationQuotationStatusFilter";
import { InterestedServiceFilter } from "./InterestedServiceFilter";
import { PreferredLanguageFilter } from "./PreferredLanguageFilter";
import { StatusFilter } from "./StatusFilter";
import { TagsFilter } from "./TagsFilter";
import { TypeFilter } from "./TypeFilter";
import { ValueFilter } from "./ValueFilter";
import { WorkOrderStatusFilter } from "./WorkOrderStatusFilter";
import { type ProjectFilterActions } from "./projectFilterReducer";
import { SelectedFilters } from "./selectedFilters/SelectedFilters";
import { hasActiveFilters } from "./utils/hasActiveFilters";
import { hasActiveSort } from "./utils/hasActiveSort";
import { addFiltersToSessionStorage } from "./utils/sessionStorage";

// 40 * 2 is the left and right padding
// 12 is the space between pills
// 65 is the space we wnt to keep it "airy"
const getPaddingWidth = (widths: Record<string, number>) => {
  const pills = Object.keys(widths).length;
  return pills * 12 + 40 * 2 + 65;
};

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  overflow-x: clip;
`;

interface OpportunitiesFilterProps {
  filters: OpportunitiesSearchFilters;
  actions: ProjectFilterActions;
  resultSize?: number;
  openNewOpportunity?: (isOpen: boolean) => void;
  openGenerateProjectsModal?: (isOpen: boolean) => void;
  options: OpportunitiesSearchOptions;
  onSort: (sortKey: string, sortOrder: "asc" | "desc") => void;
}

export const ProjectsFilter = ({
  filters,
  actions,
  resultSize,
  openNewOpportunity,
  openGenerateProjectsModal,
  options,
  onSort,
}: OpportunitiesFilterProps) => {
  const { general, consent_requirements, feature_flags } = useSettings();
  const { pushModal } = useModals();
  const { projectOverview } = useProjectTour();
  const { data: filterTeamData } = useTeam(filters?.teamFilter);
  const filterTeamSelection: TeamSelection | undefined = filterTeamData
    ? {
        kind: "team",
        team: filterTeamData,
        assignedToTeam: filters?.assignedToTeam ?? false,
        members: (filterTeamData.teamMembers ?? []).map((m) => ({
          ...m,
          selected: true,
        })),
      }
    : undefined;
  const [showFilter, setShowFilter] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(filters.term ?? "");
  const [width, setWidth] = useState<Record<string, number>>({});
  const { width: windowWidth } = useWindowSize();
  const otherRef = useRef<HTMLDivElement>(null);
  const { width: otherWidth = 0 } = useResizeObserver({
    ref: otherRef as RefObject<HTMLDivElement>,
  });
  const maxWidth = windowWidth - getPaddingWidth(width) - otherWidth;
  const isGeneratedProjectsEnabled = useAiFeatureConfig({
    feature: "generatedProjects",
  });

  useEffect(() => {
    setSearchValue(filters.term ?? "");
    addFiltersToSessionStorage({ term: filters.term });
  }, [filters.term]);

  const syncWidth = (width: number, buttonPos: number) => {
    setWidth((prev: any) => ({
      ...prev,
      [buttonPos]: width,
    }));
  };

  const doesItFit = (pos: number) => {
    let sum = 0;
    for (let i = 0; i <= pos; i++) {
      sum += width[i];

      if (sum > maxWidth) {
        return false;
      }
    }

    return true;
  };

  return (
    <SectionContainer id="tour-sales-lead-filters">
      <FlexDiv justifyContent="space-between" alignItems="center">
        <SearchField
          id="search-projects"
          data-testid="search-projects"
          placeholder={t("action.placeholder_search")}
          onClear={() => {
            actions.setTerm(undefined);
          }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSearchValue(event.currentTarget.value);
          }}
          onSearch={() => actions.setTerm(searchValue)}
          value={searchValue}
        />
        <div>
          <ResponsiveButton
            breakpoint="md"
            text={t("label.new_projects")}
            title={t("label.new_projects")}
            icon={plusIcon}
            showIcon
            size="small"
            type="primary"
            onClick={() => {
              openNewOpportunity?.(true);
              trackAnalytics("new_projects");
            }}
          />
        </div>
      </FlexDiv>

      <FiltersContainer>
        <FlexDiv gap={Variables.space75}>
          <SortButton
            options={options}
            onSort={onSort}
            setWidth={(width) => syncWidth(width, 0)}
          />
          {doesItFit(1) && (
            <FilterPill
              id="projects-created-filter__pill"
              label={t("label.created")}
              selected={!isEmpty(filters.createdDate)}
              dropDownItems={
                <DateFilterPill
                  filters={filters}
                  actions={actions}
                  option={DateFilterOption.CREATED_DATE}
                />
              }
              setWidth={(width) => syncWidth(width, 1)}
            />
          )}
          {doesItFit(2) && (
            <FilterPill
              id="projects-updated-filter__pill"
              label={t("label.updated")}
              selected={!isEmpty(filters.updatedDate)}
              dropDownItems={
                <DateFilterPill
                  filters={filters}
                  actions={actions}
                  option={DateFilterOption.UPDATED_DATE}
                />
              }
              setWidth={(width) => syncWidth(width, 2)}
            />
          )}
          {doesItFit(3) && (
            <FilterPill
              id="projects-type-filter__pill"
              label={t("label.type")}
              selected={!isEmpty(filters.opportunityType)}
              dropDownItems={<TypeFilter filters={filters} actions={actions} />}
              setWidth={(width) => syncWidth(width, 3)}
            />
          )}
          {doesItFit(4) && (
            <FilterPill
              id="projects-cmp-filter__pill"
              label={t("label.cmp")}
              selected={!isEmpty(filters.businessUnit)}
              dropDownItems={
                <BusinessUnitFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 4)}
            />
          )}
          {doesItFit(5) && (
            <FilterPill
              id="projects-hfb-filter__pill"
              label={t("label.department")}
              selected={!isEmpty(filters.department)}
              dropDownItems={
                <DepartmentFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 5)}
            />
          )}
          {doesItFit(6) && (
            <FilterPill
              id="projects-status-filter__pill"
              label={t("label.status")}
              selected={!isEmpty(filters.status)}
              dropDownItems={
                <StatusFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 6)}
            />
          )}
          {doesItFit(7) && (
            <FilterPill
              id="projects-value-filter__pill"
              label={t("label.value")}
              selected={!isEqual(filters.amount, ProjectFilterDefault.amount)}
              dropDownItems={
                <ValueFilter
                  filters={filters}
                  actions={actions}
                  option={AmountFilterOption.VALUE}
                />
              }
              setWidth={(width) => syncWidth(width, 7)}
            />
          )}

          {doesItFit(8) && (
            <FilterPill
              id="projects-customer-type-filter__pill"
              label={t("label.customer_type")}
              selected={!isEmpty(filters.customerType)}
              dropDownItems={
                <CustomerTypeFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 8)}
            />
          )}
          {doesItFit(9) && (
            <FilterPill
              id="projects-customer-type-filter__pill"
              label={t("label.preferred_language")}
              selected={!isEmpty(filters.customerLocales)}
              dropDownItems={
                <PreferredLanguageFilter filters={filters} actions={actions} />
              }
              hidden={(general?.supported_languages.length ?? 0) < 2}
              setWidth={(width) => syncWidth(width, 9)}
            />
          )}
          {doesItFit(10) && (
            <FilterPill
              id="projects-classification-filter__pill"
              label={t("label.classification")}
              selected={!isEmpty(filters.classification)}
              dropDownItems={
                <ClassificationFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 10)}
            />
          )}
          {doesItFit(11) && (
            <FilterPill
              id="projects-services-interested-filter__pill"
              label={t("label.services_interested")}
              selected={!isEmpty(filters.services)}
              dropDownItems={
                <InterestedServiceFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 11)}
            />
          )}
          {doesItFit(12) && (
            <FilterPill
              id="projects-follow-up-consent-filter__pill"
              label={translateConsentType("FOLLOWUP_CONSENT")}
              selected={!isEmpty(filters.followUpConsentFilter)}
              dropDownItems={
                <ConsentFilter
                  id="follow-up-consent"
                  value={filters.followUpConsentFilter}
                  onChange={(value) => {
                    actions.setFollowUpConsent(value);
                    trackAnalytics("select_follow_up_consent_filter_pill");
                  }}
                />
              }
              setWidth={(width) => syncWidth(width, 12)}
            />
          )}
          {doesItFit(13) && (
            <FilterPill
              id="projects-promotional-consent-filter__pill"
              label={translateConsentType("PROMOTIONAL_CONSENT")}
              selected={!isEmpty(filters.promotionalConsentFilter)}
              dropDownItems={
                <ConsentFilter
                  id="promotional-consent"
                  value={filters.promotionalConsentFilter}
                  onChange={(value) => {
                    actions.setPromotionalConsent(value);
                    trackAnalytics("select_promotional_consent_filter_pill");
                  }}
                />
              }
              hidden={!consent_requirements?.promotional_consent}
              setWidth={(width) => syncWidth(width, 13)}
            />
          )}
          {doesItFit(14) && (
            <FilterPill
              id="projects-tags-filter__pill"
              label={t("label.tags")}
              selected={!isEmpty(filters.tags)}
              dropDownItems={<TagsFilter filters={filters} actions={actions} />}
              setWidth={(width) => syncWidth(width, 14)}
            />
          )}
          {doesItFit(15) && (
            <FilterPill
              id="boka-services-filter__pill"
              label={t("label.boka_services")}
              selected={!isEmpty(filters.bokaServices)}
              dropDownItems={
                <BokaServiceFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 15)}
            />
          )}
          {doesItFit(16) && feature_flags?.enable_salesforce_integration && (
            <FilterPill
              id="installation-quotation-exists-filter__pill"
              label={t("label.installation_quotation_filter")}
              selected={filters.installationQuotationExists === false}
              dropDownItems={
                <InstallationQuotationExistsFilter
                  filters={filters}
                  actions={actions}
                />
              }
              setWidth={(width) => syncWidth(width, 16)}
            />
          )}
          {doesItFit(17) && feature_flags?.enable_salesforce_integration && (
            <FilterPill
              id="installation-quotation-status-filter__pill"
              label={t("label.installation_quotation_status")}
              selected={!isEmpty(filters.installationQuotationStatus)}
              dropDownItems={
                <InstallationQuotationStatusFilter
                  filters={filters}
                  actions={actions}
                />
              }
              setWidth={(width) => syncWidth(width, 17)}
            />
          )}
          {doesItFit(18) && feature_flags?.enable_salesforce_integration && (
            <FilterPill
              id="installation-workOrder-status-filter__pill"
              label={t("label.installation_workOrder_status")}
              selected={!isEmpty(filters.workOrderStatus)}
              dropDownItems={
                <WorkOrderStatusFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 18)}
            />
          )}

          {doesItFit(19) && feature_flags?.enable_salesforce_integration && (
            <FilterPill
              id="installation-workOrder-type-filter__pill"
              label={t("label.installation_workOrder_type")}
              selected={!isEmpty(filters.workOrderType)}
              dropDownItems={
                <WorkOrderStatusFilter filters={filters} actions={actions} />
              }
              setWidth={(width) => syncWidth(width, 19)}
            />
          )}

          <WidthAwareContainer setWidth={(width) => syncWidth(width, 20)}>
            <Pill
              size="small"
              label={t("label.all_filters")}
              trailingIcon={filtersIcon}
              onClick={() => {
                setShowFilter(!showFilter);
                trackAnalytics("all_filters");
              }}
            />
          </WidthAwareContainer>
        </FlexDiv>

        <FlexDiv gap={Variables.space75} ref={otherRef}>
          {isGeneratedProjectsEnabled && (
            <div>
              <ResponsiveButton
                breakpoint="md"
                icon={sparklesIcon}
                showIcon
                type="primary"
                text={t("action.export_list_to_widget")}
                title={t("action.export_list_to_widget")}
                ssrIcon={sparklesIcon}
                iconPosition="trailing"
                size="small"
                onClick={() => openGenerateProjectsModal?.(true)}
              />
            </div>
          )}
          <div id="tour-sales-lead-coworker-picker">
            <CoworkerAndTeamSelectorPill
              key={projectOverview?.openTeamsModal.toString()}
              selectionMode={{
                coworkerSelectionMode: "MULTI",
                selectTeamWithMembers: true,
                selectOnlyTeam: true,
              }}
              pushModal={pushModal}
              selected={filterTeamSelection ?? filters?.coworkerFilter}
              setSelected={(selected) => {
                if (isTeamSelection(selected)) {
                  actions.setTeam(selected.team.teamId);
                  actions.setAssignedToTeam(selected.assignedToTeam);
                } else {
                  actions.setCoworkers(selected?.map((el) => el.emailId));
                }
              }}
              searchContext="PROJECT"
            />
          </div>
        </FlexDiv>
      </FiltersContainer>

      <Line />

      {(hasActiveFilters(filters) || hasActiveSort(options)) && (
        <SelectedFilters
          filters={filters}
          actions={actions}
          options={options}
          onResetSort={() =>
            onSort(
              initialOpportunityOptions.sortKey,
              initialOpportunityOptions.sortOrder
            )
          }
        />
      )}

      <FilterSideBar
        isOpen={showFilter}
        setIsOpen={setShowFilter}
        filters={filters}
        actions={actions}
        resultSize={resultSize}
        onResetSort={() =>
          onSort(
            initialOpportunityOptions.sortKey,
            initialOpportunityOptions.sortOrder
          )
        }
      />
    </SectionContainer>
  );
};
